import { useContext, useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "../css/Buchsuche.css";
import { AuthContext } from "../context/AuthContext";
import { SelectedBookContext } from "../context/SelectedBookContext";
import { exemplarApi } from "../api/exemplarApi";
import { SuchContext } from "../context/SuchContext";
import { ExemplarResponse } from "../types/ExemplarResponse";
import { motion } from "framer-motion";

// 🚀 DIE ULTIMATIVE LÖSUNG: Hilfsfunktion AUSSERHALB der Komponente definieren!
// Dadurch ist sie vom Render-Zyklus befreit und der Linter ignoriert sie.
const extractUniqueValues = (data: any[], field: string): string[] => {
  const values = new Set<string>();
  if (!data || !Array.isArray(data)) return [];
  data.forEach((item) => {
    const rawValue = item[field];
    if (!rawValue) return;
    if (Array.isArray(rawValue)) {
      rawValue.forEach((v) => typeof v === "string" && values.add(v.trim()));
    } else if (typeof rawValue === "string") {
      rawValue
        .split(/[,;]+/)
        .forEach((v) => v.trim() && values.add(v.trim()));
    }
  });
  return Array.from(values).sort();
};

const Buchsuche = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setBookId } = useContext(SelectedBookContext);
  const [allBooks, setAllBooks] = useState<ExemplarResponse[]>([]);
  const [displayedBooks, setDisplayedBooks] = useState<ExemplarResponse[]>([]);

  const [alleAutoren, setAlleAutoren] = useState<string[]>([]);
  const [alleKategorien, setAlleKategorien] = useState<string[]>([]);

  const authContext = useContext(AuthContext)!;
  const { user, fetchMe } = authContext;
  const mitgliedId = user?.mitgliedID;
  const isAdmin = user?.roles?.includes("ADMIN");

  // --- STATES ---
  const [filters, setFilters] = useState({
    titel: "",
    autor: "",
    kategorie: "",
    isbn: "",
  });

  const hasFilters =
    filters.titel || filters.autor || filters.kategorie || filters.isbn;

  // --- HILFSFUNKTIONEN ---

  const applyFiltersInternal = useCallback(
    (booksToFilter: ExemplarResponse[]) => {
      let filtered = [...booksToFilter];

      if (filters.autor) {
        filtered = filtered.filter((b) =>
          b.autoren?.toLowerCase().includes(filters.autor.toLowerCase()),
        );
      }
      if (filters.kategorie) {
        filtered = filtered.filter((b) =>
          b.kategorie?.toLowerCase().includes(filters.kategorie.toLowerCase()),
        );
      }
      if (filters.titel) {
        filtered = filtered.filter((b) =>
          b.titel?.toLowerCase().includes(filters.titel.toLowerCase()),
        );
      }
      if (filters.isbn) {
        filtered = filtered.filter((b) => b.isbn?.includes(filters.isbn));
      }

      setDisplayedBooks(filtered);
    },
    [filters],
  );

  const updateBookStatusLocal = (
    exemplarId: number,
    newStatus: string,
    isAvailable: boolean,
  ) => {
    setAllBooks((prevBooks) => {
      const updated = prevBooks.map((b) =>
        b.exemplarId === exemplarId
          ? { ...b, status: newStatus, verfuegbar: isAvailable }
          : b,
      );
      applyFiltersInternal(updated);
      return updated;
    });
  };

  // --- HANDLER ---

  const handleSearch = useCallback(() => {
    applyFiltersInternal(allBooks);
  }, [applyFiltersInternal, allBooks]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleReset = () => {
    setFilters({ titel: "", autor: "", kategorie: "", isbn: "" });
    setDisplayedBooks(allBooks);
  };

  // --- ACTIONS ---

  const handleBorrow = async (e: ExemplarResponse) => {
    if (!mitgliedId) return alert("Bitte einloggen!");
    try {
      await exemplarApi.ausleihen(e.exemplarId, mitgliedId);
      updateBookStatusLocal(e.exemplarId, "AUSGELIEHEN", false);
      await fetchMe();
      alert("Erfolgreich ausgeliehen!");
    } catch (err: any) {
      alert("Fehler. Seite wird neu geladen...");
      window.location.reload();
    }
  };

  const handleReserve = async (e: ExemplarResponse) => {
    if (!mitgliedId) return alert("Bitte einloggen!");
    try {
      await exemplarApi.reservieren(e.exemplarId, mitgliedId);
      updateBookStatusLocal(e.exemplarId, "RESERVIERT", false);
      await fetchMe();
      alert("Reserviert!");
    } catch (err: any) {
      alert("Fehler. Seite wird neu geladen...");
      window.location.reload();
    }
  };

  const handleCancelReservation = async (e: ExemplarResponse) => {
    if (!mitgliedId) return alert("Bitte einloggen!");
    try {
      await exemplarApi.reservierungAufheben(e.exemplarId, mitgliedId);
      updateBookStatusLocal(e.exemplarId, "VERFÜGBAR", true);
      await fetchMe();
      alert("Storniert.");
    } catch (err: any) {
      alert("Fehler. Seite wird neu geladen...");
      window.location.reload();
    }
  };

  // --- EFFECTS ---

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const result = await SuchContext({});
        if (result && Array.isArray(result)) {
          setAllBooks(result);
          setDisplayedBooks(result);
          setAlleAutoren(extractUniqueValues(result, "autoren"));
          setAlleKategorien(extractUniqueValues(result, "kategorie"));

          const titelFromQuery = searchParams.get("titel");
          if (titelFromQuery) {
            setFilters((prev) => ({ ...prev, titel: titelFromQuery }));
          }
        }
      } catch (e) {
        console.error("Fehler beim Laden:", e);
      }
    };
    loadInitialData();
  }, [searchParams]); // 🧹 SAUBER: extractUniqueValues wird hier nicht mehr gebraucht!

  useEffect(() => {
    if (allBooks.length > 0) {
      handleSearch();
    }
  }, [filters.titel, filters.autor, filters.kategorie, filters.isbn, allBooks.length, handleSearch]);

  // --- ACTIONS ---

  const handleEdit = (e: ExemplarResponse) => {
    setBookId(e.buchId);
    navigate("/buchverwaltung");
  };

  // --- RENDER ---
  return (
    <div className="buchsuche-container">
      <h2>Bibliothek</h2>

      <div className="filter-container">
        <motion.input
          layoutId="search-input-field"
          type="text"
          name="titel"
          placeholder="Titel suchen..."
          value={filters.titel}
          onChange={handleChange}
          transition={{ type: "spring", stiffness: 50, damping: 15, mass: 1.2 }}
          style={{ originX: 0, originY: 0, zIndex: 100 }}
        />
        <select name="autor" value={filters.autor} onChange={handleChange}>
          <option value="">Alle Autoren</option>
          {alleAutoren.map((autor, idx) => (
            <option key={idx} value={autor}>
              {autor}
            </option>
          ))}
        </select>
        <select
          name="kategorie"
          value={filters.kategorie}
          onChange={handleChange}
        >
          <option value="">Alle Kategorien</option>
          {alleKategorien.map((kat, idx) => (
            <option key={idx} value={kat}>
              {kat}
            </option>
          ))}
        </select>
        <input
          type="text"
          name="isbn"
          placeholder="ISBN"
          value={filters.isbn}
          onChange={handleChange}
        />
        <div className="button-group">
          <button onClick={handleSearch} className="search-btn">
            Suchen
          </button>
          {hasFilters && (
            <button onClick={handleReset} className="reset-btn">
              Reset Filters
            </button>
          )}
        </div>
      </div>

      <div className="results">
        {displayedBooks.length === 0 && <p>Keine Bücher gefunden.</p>}

        {displayedBooks.map((e) => {
          const isReservedByMe = user?.reservierungen?.some(
            (r) => r.exemplarID === e.exemplarId,
          );

          const coverImage = e.bildUrl
            ? e.bildUrl
            : "https://via.placeholder.com/150x220?text=Kein+Bild";

          return (
            <div className="buch-card" key={e.exemplarId}>
              {isAdmin && (
                <div className="admin-buttons">
                  <button className="edit-btn" onClick={() => handleEdit(e)}>
                    ✏️
                  </button>
                  <button className="delete-btn">🗑️</button>
                </div>
              )}

              <div className="card-image-container">
                <img
                  src={coverImage}
                  alt={e.titel}
                  className="book-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://via.placeholder.com/120x180?text=No+Img";
                  }}
                />
              </div>

              <div className="card-content">
                <div>
                  <h3>{e.titel}</h3>
                  <p>
                    <strong>{e.autoren}</strong>
                  </p>
                  <p>
                    {e.kategorie} • <small>#{e.exemplarId}</small>
                  </p>

                  <div
                    className={`status ${e.verfuegbar ? "available" : "unavailable"}`}
                  >
                    {e.status}
                  </div>

                  <p style={{ marginTop: "10px", fontSize: "0.9rem" }}>
                    📍 {e.standort}
                  </p>
                </div>

                <div className="card-actions">
                  {!user ? (
                    <button className="borrow-btn">
                      <Link
                        to="/login"
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        Login für Aktionen
                      </Link>
                    </button>
                  ) : isReservedByMe ? (
                    <button
                      className="cancel-res-btn"
                      onClick={() => handleCancelReservation(e)}
                    >
                      Stornieren
                    </button>
                  ) : (
                    <>
                      {e.verfuegbar ? (
                        <div className="action-row">
                          <button
                            className="borrow-btn"
                            onClick={() => handleBorrow(e)}
                          >
                            Ausleihen
                          </button>
                          <button
                            className="reserve-btn"
                            onClick={() => handleReserve(e)}
                          >
                            Vormerken
                          </button>
                        </div>
                      ) : (
                        <button
                          className="reserve-btn"
                          onClick={() => handleReserve(e)}
                        >
                          Vormerken
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Buchsuche;